import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import {
  PublicSubscriptionBillingAddressForm,
  PublicSubscriptionDataSourceService,
  SubscriptionNumber,
} from '@web/shared/module/shop';
import { toObservable } from '@angular/core/rxjs-interop';
import { finalize, Observable, switchMap } from 'rxjs';
import { PublicSubscriptionBillingAddressFormComponent } from '../public-subscription-billing-address-form/public-subscription-billing-address-form.component';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { HttpErrorService } from '@web/common';

@Component({
  selector: 'app-public-subscription-billing-address-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PublicSubscriptionBillingAddressFormComponent,
    AlertsComponent,
    SpinnerFilledComponent,
  ],
  templateUrl: './public-subscription-billing-address-edit.component.html',
  styleUrl: './public-subscription-billing-address-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class PublicSubscriptionBillingAddressEditComponent {
  public readonly number = input.required<SubscriptionNumber>();
  public readonly submitEvent = output<void>();
  public readonly cancelEvent = output<void>();

  protected readonly pending = signal(false);
  protected readonly number$ = this.numberSource();
  protected readonly data$ = this.dataSource();

  /* prettier-ignore */
  private readonly subscriptionDataSource = inject(PublicSubscriptionDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public handleCancelClickEvent(): void {
    this.cancelEvent.emit();
  }

  public handleSubmitClickEvent(
    data: PublicSubscriptionBillingAddressForm,
  ): void {
    this.markAsPending();
    this.subscriptionDataSource
      .updateBillingAddressByFormData({
        number: this.number(),
        data,
      })
      .pipe(finalize(() => this.markAsResolved()))
      .subscribe({
        next: () => this.submitEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private markAsPending(): void {
    this.pending.set(true);
  }

  private markAsResolved(): void {
    this.pending.set(false);
  }

  private numberSource(): Observable<SubscriptionNumber> {
    return toObservable(this.number);
  }

  private dataSource(): Observable<PublicSubscriptionBillingAddressForm> {
    return this.number$.pipe(
      switchMap((number) =>
        this.subscriptionDataSource.fetchBillingAddressFormByNumber(number),
      ),
    );
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }
}
